import React from "react";
import { Link } from "react-router-dom";
import { Check, Leaf, HandCoins, ShoppingBasket } from "lucide-react";
import { Reveal } from "@/components/site/primitives";
import { CSA_SHARE_OPTIONS, CSA_SEASON } from "@/content/csa";
import { cn } from "@/lib/utils";

// Real 2026 share data (content/csa.js), shaped for the teaser cards.
const CSA_SHARES = CSA_SHARE_OPTIONS.map((s) => ({
  id: s.id,
  name: s.name,
  serves: s.feeds.replace("Feeds approximately ", ""),
  price: s.priceLabel,
  cadence: `/ ${s.seasonLabel}`,
  featured: s.featured,
  features: [
    `${s.items} seasonal items every week`,
    "Featured cultural crop + recipe card",
    `SNAP/HIP: $${s.snapMonthly}/month for 4 months`,
    s.installments ? "Pay in full or two installments" : "Pay in full",
  ],
}));

const STEPS = [
  { icon: HandCoins, title: "Reserve your share", text: "Choose a share size and reserve your spot before the season begins." },
  { icon: Leaf, title: "We grow & harvest", text: "Our farmers grow a rotating mix of fresh, culturally relevant crops all season." },
  { icon: ShoppingBasket, title: "Pick up weekly", text: `Collect your share every Thursday at one of four pickup locations, ${CSA_SEASON.dates}.` },
];

export const CsaSection = () => {
  return (
    <section id="csa" aria-labelledby="csa-heading" className="wf-section bg-white">
      <div className="wf-container">
        <Reveal className="mx-auto max-w-2xl text-center">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--wf-green-text)]">Join a CSA</span>
          <h2 id="csa-heading" className="mt-3 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">A season of fresh food, every week</h2>
          <p className="mt-3 text-base leading-7 text-muted-foreground">Community Supported Agriculture connects you directly with our farmers — you get the freshest produce, they get a stable season.</p>
        </Reveal>

        {/* How it works */}
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-3">
          {STEPS.map((s, i) => (
            <Reveal key={s.title} delay={0.05 * i} className="rounded-sm border border-[var(--wf-border)] bg-[var(--wf-cream)] p-6">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[var(--wf-farm-green)] text-white"><s.icon className="h-5 w-5" aria-hidden="true" /></span>
              <h3 className="mt-4 font-serif text-lg font-semibold text-[var(--wf-ink)]">{i + 1}. {s.title}</h3>
              <p className="mt-1.5 text-sm leading-6 text-muted-foreground">{s.text}</p>
            </Reveal>
          ))}
        </div>

        {/* Share tiers */}
        <div className="mx-auto mt-12 grid max-w-4xl grid-cols-1 gap-6 lg:grid-cols-2">
          {CSA_SHARES.map((share, i) => (
            <Reveal key={share.id} delay={0.05 * i}>
              <div
                data-testid={`wtb-csa-card-${share.id}`}
                className={cn(
                  "relative flex h-full flex-col rounded-sm border bg-[var(--wf-paper)] p-7 shadow-[var(--wf-shadow-sm)]",
                  share.featured ? "border-[var(--wf-maroon)] shadow-[var(--wf-shadow)]" : "border-[var(--wf-border)]"
                )}
              >
                {share.featured && (
                  <span className="absolute -top-3 left-7 rounded-full bg-[var(--wf-maroon)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--wf-paper)]">Most popular</span>
                )}
                <h3 className="font-serif text-2xl font-semibold text-[var(--wf-ink)]">{share.name}</h3>
                <p className="mt-1 text-sm text-muted-foreground">Serves {share.serves}</p>
                <div className="mt-5 flex items-baseline gap-1.5">
                  <span className="font-serif text-4xl font-semibold text-[var(--wf-maroon)]">{share.price}</span>
                  <span className="text-sm text-muted-foreground">{share.cadence}</span>
                </div>
                <ul className="mt-6 flex-1 space-y-2.5">
                  {share.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-[var(--wf-ink)]">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--wf-green-text)]" aria-hidden="true" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/csa"
                  data-testid={`wtb-csa-reserve-${share.id}`}
                  className={cn(
                    "mt-7 inline-flex w-full items-center justify-center rounded-full px-5 py-3 text-sm font-semibold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background",
                    share.featured
                      ? "bg-[var(--wf-maroon)] text-[var(--wf-paper)] hover:bg-[var(--wf-maroon-dark)]"
                      : "border border-[var(--wf-maroon)] text-[var(--wf-maroon)] hover:bg-[var(--wf-maroon)] hover:text-[var(--wf-paper)]"
                  )}
                >
                  Reserve a share
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-6 text-center text-sm text-muted-foreground">Sign up online, by check, or with SNAP/HIP on the <Link to="/csa" className="font-semibold text-[var(--wf-maroon)] underline-offset-4 hover:underline" data-testid="wtb-csa-page-link">CSA page</Link>.</p>
      </div>
    </section>
  );
};
